import React, { useState, useRef, useEffect, useContext } from 'react';
import { 
  FaSearch, FaPaperPlane, FaPaperclip, FaSmile, FaEllipsisV
} from 'react-icons/fa';
import '../../assets/css/customer/customerDashboard/customerMessages/CustomerMessages.css';
import CustomerMasterLayout from '../../layouts/CustomerMasterLayout';
import axios from 'axios';
import Swal from 'sweetalert2';
import MyContext from '../../context/MyContext';
import { MessagesPageSkeleton } from '../../components/general/skeleton/SkeletonBlock';
import { getEcho } from '../../components/general/echo/Echo';

const CustomerMessages = () => {
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages]           = useState([]);
  const [selectedChat, setSelectedChat]   = useState(null);
  const [messageText, setMessageText]     = useState('');
  const [searchText, setSearchText]       = useState('');
  const [errorMessage, setErrorMessage]   = useState('');
  const [attachedFile, setAttachedFile]   = useState(null);
  const [loading, setLoading]             = useState(true);

  const messagesEndRef = useRef(null);
  const fileInputRef   = useRef(null);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // viewAllConversations
  const viewAllConversations = () => {
    setLoading(true);

    axios
      .get(`${baseUrl}/customer/conversations/viewAllConversations`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty('error')) {
          setErrorMessage(response.data.error);
          Swal.fire({ icon: 'error', title: 'Error', text: response.data.error });
        } else {
          setConversations(response.data.conversations || []);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      return;
    }
    viewAllConversations();
  }, [authToken]);

  // ── real-time — jo conversation khuli hai uske channel pe naya
  // message aate hi list mein append kar do, poori history dobara
  // fetch karne ki zaroorat nahi ──
  useEffect(() => {
    if (!authToken || !selectedChat) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `conversation.${selectedChat.id}`;
    const channel = echo.private(channelName);

    channel.listen('.new-message', (e) => {
      const incoming = e.message;
      setMessages((prev) => {
        // ── dedup — apna hi bheja hua message dobara na add ho ──
        if (prev.some((m) => m.id === incoming.id)) return prev;
        return [...prev, incoming];
      });
      viewAllConversations(); // sidebar preview/order update
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, selectedChat?.id]);

  // viewAllMessages
  const viewAllMessages = (conversationId) => {
    const formData = new FormData();
    formData.append('kmconversation_id', conversationId);

    axios
      .post(`${baseUrl}/customer/chat/viewAllMessages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty('error')) {
          setErrorMessage(response.data.error);
          Swal.fire({ icon: 'error', title: 'Error', text: response.data.error });
        } else {
          setMessages(response.data.messages);
        }
      });
  };

  // sendMessage
  const sendMessage = () => {
    if (!messageText.trim() && !attachedFile) return;
    if (!selectedChat) return;

    const formData = new FormData();
    formData.append('kmconversation_id', selectedChat.id);
    formData.append('send_by',           'customer');
    formData.append('message',           messageText);
    formData.append('image',             attachedFile);

    axios
      .post(`${baseUrl}/customer/chat/sendMessage`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty('error')) {
          setErrorMessage(response.data.error);
          Swal.fire({ icon: 'error', title: 'Error', text: response.data.error });
        } else {
          setMessageText('');
          setAttachedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          viewAllMessages(selectedChat.id);
          viewAllConversations();
        }
      });
  };

  // Filter
  const filteredChats = conversations.filter((conv) =>
    conv.kmseller?.shop_name?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Format timestamp
  const formatTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    return (
      <CustomerMasterLayout>
        <MessagesPageSkeleton />
      </CustomerMasterLayout>
    );
  }

  return (
    <CustomerMasterLayout>
      <div className="cust-msg-container">
        <div className="cust-msg-wrapper">

          {/* SIDEBAR — Chat List */}
          <div className="cust-msg-sidebar">
            <div className="cust-msg-sidebar-header">
              <h3>Messages</h3>
            </div>

            {/* Search */}
            <div className="cust-msg-search">
              <FaSearch />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            {/* Chat List */}
            <div className="cust-msg-chat-list">
              {filteredChats.map((conv) => (
                <div
                  key={conv.id}
                  className={`cust-msg-chat-item ${selectedChat?.id === conv.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedChat(conv);
                    viewAllMessages(conv.id);
                  }}
                >
                  <div className="cust-msg-chat-avatar">
                    <div className="cust-msg-avatar-circle">
                      {conv.kmseller?.shop_name?.charAt(0)}
                    </div>
                  </div>
                  <div className="cust-msg-chat-info">
                    <div className="cust-msg-chat-header">
                      <h6>{conv.kmseller?.shop_name}</h6>
                      <span className="cust-msg-chat-time">{conv.updated_at}</span>
                    </div>
                    <div className="cust-msg-chat-preview">
                      <p>{conv.lastMessage}</p>
                      {conv.status === 'unread' && (
                        <span className="cust-msg-unread-badge">new</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {filteredChats.length === 0 && (
                <div className="cust-msg-no-chats">
                  <p>No conversations found</p>
                </div>
              )}
            </div>
          </div>

          {/* CHAT AREA */}
          <div className="cust-msg-chat-area">
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="cust-msg-chat-header">
                  <div className="cust-msg-header-left">
                    <div className="cust-msg-avatar-circle">
                      {selectedChat.kmseller?.shop_name?.charAt(0)}
                    </div>
                    <div className="cust-msg-header-info">
                      <h5>{selectedChat.kmseller?.shop_name}</h5>
                    </div>
                  </div>
                  <div className="cust-msg-header-actions">
                    <button className="cust-msg-action-btn"><FaEllipsisV /></button>
                  </div>
                </div>

                {/* Messages */}
                <div className="cust-msg-messages-area">
                  <div className="cust-msg-messages-wrapper">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`cust-msg-message ${msg.send_by === 'customer' ? 'sent' : 'received'}`}
                      >
                        <div className="cust-msg-bubble">
                          {msg.image && (
                            <img
                              src={`${baseUrlForFiles}/${msg.image}`}
                              alt="attachment"
                              className="cust-msg-image"
                            />
                          )}
                          {msg.message && <p>{msg.message}</p>}
                          <div className="cust-msg-meta">
                            <span className="cust-msg-time">{formatTime(msg.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* File Preview */}
                {attachedFile && (
                  <div className="cust-msg-file-preview">
                    <span>📎 {attachedFile.name}</span>
                    <button
                      onClick={() => {
                        setAttachedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Message Input */}
                <div className="cust-msg-input-area">
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => setAttachedFile(e.target.files[0])}
                  />

                  <button
                    className="cust-msg-attach-btn"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <FaPaperclip />
                  </button>

                  <div className="cust-msg-input-wrapper">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                    <button className="cust-msg-emoji-btn">
                      <FaSmile />
                    </button>
                  </div>

                  <button
                    className="cust-msg-send-btn"
                    onClick={() => sendMessage()}
                    disabled={!messageText.trim() && !attachedFile}
                  >
                    <FaPaperPlane />
                  </button>
                </div>
              </>
            ) : (
              <div className="cust-msg-empty-state">
                <div className="cust-msg-empty-icon">💬</div>
                <h3>Select a conversation</h3>
                <p>Choose a seller to start messaging</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </CustomerMasterLayout>
  );
};

export default CustomerMessages;
